import { ref, computed, getCurrentInstance } from 'vue';
import cesionAsientoService from '@/services/perfil/cesionAsientoService';

/**
 * useCesionAsiento(abonadoId)
 *
 * Manages state and logic for temporary seat transfer (cesión temporal de asiento).
 * Handles 5 endpoints with error normalization (400/412 → inline, 5xx → generic).
 * Toast notifications via proxy.$toast (same pattern as existing composables).
 * No Vuex mutations.
 */
export function useCesionAsiento(abonadoId) {
    // ─────────────────────────────────────────────────────────────────
    // Reactive State: Eventos
    // ─────────────────────────────────────────────────────────────────
    const eventos = ref([]);
    const loadingEventos = ref(false);
    const errorEventos = ref(null);

    // ─────────────────────────────────────────────────────────────────
    // Reactive State: Selected Evento & Estado
    // ─────────────────────────────────────────────────────────────────
    const selectedEvento = ref(null); // { eventoId, sesionId } or null
    const eventoWithMultipleSessions = ref(null); // Loaded evento info to show session picker
    const showSessionPicker = ref(false); // Whether to show the session selection modal
    const estadoActual = ref(null);
    const loadingEstado = ref(false);
    const errorEstado = ref(null);

    // ─────────────────────────────────────────────────────────────────
    // Reactive State: Evento Info (datos del partido)
    // ─────────────────────────────────────────────────────────────────
    const eventoInfo = ref(null);
    const loadingEvento = ref(false);
    const errorEvento = ref(null);

    // ─────────────────────────────────────────────────────────────────
    // Reactive State: Operación (ceder/recuperar)
    // ─────────────────────────────────────────────────────────────────
    const errorOperacion = ref(null); // 400/412 write errors
    const loadingOperacion = ref(false);

    // ─────────────────────────────────────────────────────────────────
    // Computed Properties
    // ─────────────────────────────────────────────────────────────────
    const isEventSelected = computed(() => selectedEvento.value !== null);
    const puedeVerDetalle = computed(() => estadoActual.value !== null && !loadingEstado.value);

    // ─────────────────────────────────────────────────────────────────
    // Normalization Helpers
    // ─────────────────────────────────────────────────────────────────
    function extractServerMessage(err, fallback) {
        return err?.response?.data?.message
            ?? err?.response?.data?.error
            ?? fallback;
    }

    /**
     * Normalizes collection payload to array.
     * Accepts both raw arrays and wrapped collections:
     * - Array: [...] → [...]
     * - Wrapped: { data: [...] } → [...]
     * Defaults to [] if neither shape matches.
     */
    function normalizeCollection(payload) {
        if (Array.isArray(payload)) {
            return payload;
        }
        if (payload && typeof payload === 'object' && Array.isArray(payload.data)) {
            return payload.data;
        }
        return [];
    }

    /**
     * Extracts all sessions from the loaded evento info.
     * Returns array of { sesion_id, fecha, hora, fecha_provisional, cesion_temporal_activa }
     * Used to populate the session picker modal.
     */
    function extractSessionsFromEventoInfo(info) {
        if (!info || !info.sesiones) return [];
        return Array.isArray(info.sesiones) ? info.sesiones : [];
    }

    // ─────────────────────────────────────────────────────────────────
    // Methods: Load Eventos
    // ─────────────────────────────────────────────────────────────────
    async function loadEventos() {
        loadingEventos.value = true;
        errorEventos.value = null;
        try {
            const data = await cesionAsientoService.getEventos(abonadoId.value);
            eventos.value = normalizeCollection(data);
        } catch (err) {
            const msg = extractServerMessage(err, 'Error al cargar los eventos disponibles');
            errorEventos.value = msg;
            if (process.env.NODE_ENV !== 'production') {
                console.error('loadEventos error:', err);
            }
        } finally {
            loadingEventos.value = false;
        }
    }

    // ─────────────────────────────────────────────────────────────────
    // Methods: Select Evento & Load Estado + Evento Info
    // ─────────────────────────────────────────────────────────────────
    /**
     * Called when user clicks an event in the list.
     * Loads evento info to check how many sessions exist.
     * If multiple sessions: show picker modal.
     * If single session: auto-select and load detail.
     */
    async function onSelectEventoFromList(evento) {
        const eventoId = evento?.id;
        if (!eventoId) return;

        // Clear previous selection
        selectedEvento.value = null;
        estadoActual.value = null;
        errorEstado.value = null;
        eventoInfo.value = null;
        errorEvento.value = null;
        eventoWithMultipleSessions.value = null;
        showSessionPicker.value = false;

        // Load evento info to inspect sessions
        loadingEvento.value = true;
        errorEvento.value = null;
        try {
            const info = await cesionAsientoService.getEvento(abonadoId.value, eventoId);
            eventoInfo.value = info;
            
            const sessions = extractSessionsFromEventoInfo(info);
            
            if (sessions.length > 1) {
                // Multiple sessions: show picker modal
                eventoWithMultipleSessions.value = info;
                showSessionPicker.value = true;
            } else if (sessions.length === 1) {
                // Single session: auto-select it
                const sesionId = sessions[0].sesion_id;
                await selectEvento(eventoId, sesionId);
            } else {
                // No sessions available
                errorEvento.value = 'No hay sesiones disponibles para este evento';
            }
        } catch (err) {
            const msg = extractServerMessage(err, 'Error al cargar los datos del evento');
            errorEvento.value = msg;
            if (process.env.NODE_ENV !== 'production') {
                console.error('onSelectEventoFromList error:', err);
            }
        } finally {
            loadingEvento.value = false;
        }
    }

    /**
     * Called when user picks a session from the modal.
     * Closes modal and loads detail for that session.
     */
    async function selectSessionFromPicker(sesionId) {
        if (!eventoInfo.value || !eventoInfo.value.id) {
            errorEvento.value = 'Evento no válido';
            return;
        }
        showSessionPicker.value = false;
        await selectEvento(eventoInfo.value.id, sesionId);
    }

    /**
     * Internal: select evento + sesion and load both estado and evento info.
     * Only called after session is determined.
     */
    async function selectEvento(eventoId, sesionId) {
        selectedEvento.value = { eventoId, sesionId };
        estadoActual.value = null;
        errorEstado.value = null;
        await Promise.all([loadEstado(), loadEvento()]);
    }

    async function loadEvento() {
        if (!selectedEvento.value) return;

        loadingEvento.value = true;
        errorEvento.value = null;
        try {
            const { eventoId } = selectedEvento.value;
            eventoInfo.value = await cesionAsientoService.getEvento(
                abonadoId.value,
                eventoId
            );
        } catch (err) {
            const msg = extractServerMessage(err, 'Error al cargar los datos del evento');
            errorEvento.value = msg;
            if (process.env.NODE_ENV !== 'production') {
                console.error('loadEvento error:', err);
            }
        } finally {
            loadingEvento.value = false;
        }
    }

    async function loadEstado() {
        if (!selectedEvento.value) return;

        loadingEstado.value = true;
        errorEstado.value = null;
        try {
            const { eventoId, sesionId } = selectedEvento.value;
            const data = await cesionAsientoService.getEstado(
                abonadoId.value,
                eventoId,
                sesionId
            );
            estadoActual.value = data;
        } catch (err) {
            const msg = extractServerMessage(err, 'Error al cargar el estado de la transferencia');
            errorEstado.value = msg;
            if (process.env.NODE_ENV !== 'production') {
                console.error('loadEstado error:', err);
            }
        } finally {
            loadingEstado.value = false;
        }
    }

    // ─────────────────────────────────────────────────────────────────
    // Methods: Ceder (write operation)
    // ─────────────────────────────────────────────────────────────────
    async function ceder(payload = {}) {
        errorOperacion.value = null;

        if (!selectedEvento.value) {
            errorOperacion.value = 'No hay evento seleccionado';
            return false;
        }

        loadingOperacion.value = true;
        try {
            const { eventoId, sesionId } = selectedEvento.value;
            const response = await cesionAsientoService.ceder(
                abonadoId.value,
                eventoId,
                sesionId,
                payload
            );

            // Update estado from response
            if (response && typeof response === 'object') {
                estadoActual.value = response;
            }

            // Show success toast
            const toast = getCurrentInstance()?.proxy?.$toast;
            const msg = response?.mensaje ?? 'Asiento cedido exitosamente';
            if (toast) {
                toast.success(msg);
            }

            return true;
        } catch (err) {
            // 400 and 412 errors → inline display, no state refresh
            if (err?.response?.status === 400 || err?.response?.status === 412) {
                const msg = extractServerMessage(err, 'No se pudo completar la operación');
                errorOperacion.value = msg;
            } else {
                // 5xx or network error
                const msg = extractServerMessage(err, 'Error inesperado al ceder el asiento');
                errorOperacion.value = msg;
            }

            if (process.env.NODE_ENV !== 'production') {
                console.error('ceder error:', err);
            }
            return false;
        } finally {
            loadingOperacion.value = false;
        }
    }

    // ─────────────────────────────────────────────────────────────────
    // Methods: Recuperar (write operation)
    // ─────────────────────────────────────────────────────────────────
    async function recuperar() {
        errorOperacion.value = null;

        if (!selectedEvento.value) {
            errorOperacion.value = 'No hay evento seleccionado';
            return;
        }

        loadingOperacion.value = true;
        try {
            const { eventoId, sesionId } = selectedEvento.value;
            const response = await cesionAsientoService.recuperar(
                abonadoId.value,
                eventoId,
                sesionId
            );

            // Update estado from response
            if (response && typeof response === 'object') {
                estadoActual.value = response;
            }

            // Show success toast
            const toast = getCurrentInstance()?.proxy?.$toast;
            const msg = response?.mensaje ?? 'Asiento recuperado exitosamente';
            if (toast) {
                toast.success(msg);
            }
        } catch (err) {
            // 400 and 412 errors → inline display, no state refresh
            if (err?.response?.status === 400 || err?.response?.status === 412) {
                const msg = extractServerMessage(err, 'No se pudo completar la operación');
                errorOperacion.value = msg;
            } else {
                // 5xx or network error
                const msg = extractServerMessage(err, 'Error inesperado al recuperar el asiento');
                errorOperacion.value = msg;
            }

            if (process.env.NODE_ENV !== 'production') {
                console.error('recuperar error:', err);
            }
        } finally {
            loadingOperacion.value = false;
        }
    }

    return {
        // State: eventos
        eventos,
        loadingEventos,
        errorEventos,

        // State: selected evento & estado
        selectedEvento,
        estadoActual,
        loadingEstado,
        errorEstado,

        // State: evento info
        eventoInfo,
        loadingEvento,
        errorEvento,

        // State: session picker modal
        showSessionPicker,
        eventoWithMultipleSessions,

        // State: operación
        errorOperacion,
        loadingOperacion,

        // Computed
        isEventSelected,
        puedeVerDetalle,

        // Methods
        loadEventos,
        onSelectEventoFromList,
        selectSessionFromPicker,
        selectEvento,
        loadEstado,
        loadEvento,
        ceder,
        recuperar,
    };
}
